import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchAllPublished,
  buildSearchIndex,
  searchEntries,
  filterEntries,
  sortEntries,
  deriveFacets,
  EMPTY_FILTERS,
  countActiveFilters,
} from '../lib/bibliography.js';
import { useAuth } from '../context/AuthContext.jsx';
import { loadWithRetry } from '../lib/loadWithRetry.js';
import EntryRow from '../components/bibliography/EntryRow.jsx';
import EntryDetailModal from '../components/bibliography/EntryDetailModal.jsx';
import Filters from '../components/bibliography/Filters.jsx';
import ExportMenu from '../components/bibliography/ExportMenu.jsx';
import Paginator from '../components/common/Paginator.jsx';

const PAGE_SIZE = 25;

export default function Bibliography() {
  const { t } = useTranslation();
  const { isActive } = useAuth();
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const [searchParams] = useSearchParams();

  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(() => {
    const author = searchParams.get('author');
    return author ? { ...EMPTY_FILTERS, author } : EMPTY_FILTERS;
  });
  const [sort, setSort] = useState('year_desc');
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [detailEntry, setDetailEntry] = useState(null);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEntries(null);
    setError(null);
    loadWithRetry(fetchAllPublished, { label: 'bibliography' })
      .then((rows) => {
        if (!cancelled) setEntries(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  const index = useMemo(() => (entries ? buildSearchIndex(entries) : null), [entries]);
  const facets = useMemo(() => (entries ? deriveFacets(entries) : null), [entries]);

  const results = useMemo(() => {
    if (!entries || !index) return [];
    let list = searchEntries(entries, index, query);
    list = filterEntries(list, filters);
    list = sortEntries(list, sort);
    return list;
  }, [entries, index, query, filters, sort]);

  // Reset page when results change.
  useEffect(() => {
    setPage(0);
  }, [query, filters, sort]);

  // Deep link from a What's New notice: /bibliography?entry=<id> opens that
  // entry's detail modal once the data is in.
  useEffect(() => {
    const id = searchParams.get('entry');
    if (!id || !entries) return;
    const match = entries.find((e) => e.id === id);
    if (match) setDetailEntry(match);
  }, [entries, searchParams]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageStart = page * PAGE_SIZE;
  const pageItems = results.slice(pageStart, pageStart + PAGE_SIZE);
  const activeCount = countActiveFilters(filters);

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearAll() {
    setQuery('');
    setFilters(EMPTY_FILTERS);
    setSort('year_desc');
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
        <button
          type="button"
          className="btn-primary mt-6"
          onClick={() => setRetryKey((k) => k + 1)}
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }

  if (!entries || !facets) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-shell px-6 pb-20 pt-28">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-label">{t('home.tiles.bibliography.title')}</div>
          <h1 className="font-serif text-4xl font-semibold text-cream">
            {t('bibliography.heading')}
          </h1>
          <p className="mt-2 max-w-2xl text-ink-secondary">
            {t('bibliography.subheading', { count: entries.length })}
          </p>
        </div>
        {isActive && (
          <Link to="/bibliography/new" className="btn-primary">
            {t('bibliography.addEntry')}
          </Link>
        )}
      </header>

      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-navy-base/50 p-3 backdrop-blur">
        <input
          type="search"
          placeholder={t('bibliography.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-white/10 bg-navy-deep/60 px-4 py-2 text-cream placeholder:text-ink-muted focus:border-gold/50"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="rounded-md border border-white/10 bg-navy-deep/60 px-3 py-2 text-sm text-cream focus:border-gold/50"
        >
          <option value="year_desc">{t('bibliography.sort.yearDesc')}</option>
          <option value="year_asc">{t('bibliography.sort.yearAsc')}</option>
          <option value="author_asc">{t('bibliography.sort.authorAsc')}</option>
          <option value="title_asc">{t('bibliography.sort.titleAsc')}</option>
        </select>
        <ExportMenu entries={results} selectedIds={selectedIds} />
        <button
          type="button"
          className="btn-ghost text-sm md:hidden"
          onClick={() => setShowFiltersMobile((s) => !s)}
        >
          {t('common.filter')}
          {activeCount > 0 && (
            <span className="ml-1 rounded-full bg-gold/20 px-1.5 text-xs text-gold-light">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      <div className="flex items-center justify-between text-sm text-ink-secondary">
        <span>
          {t('bibliography.resultCount', { count: results.length })}
          {selectedIds.size > 0 && (
            <span className="ml-3 text-gold-light">
              {t('bibliography.selectedCount', { count: selectedIds.size })}
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="ml-2 text-xs underline hover:text-gold"
              >
                {t('common.clear')}
              </button>
            </span>
          )}
        </span>
        {(query || activeCount > 0) && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs font-medium text-gold-light hover:underline"
          >
            {t('bibliography.clearAll')}
          </button>
        )}
      </div>

      <div className="mt-4 grid gap-8 md:grid-cols-[260px_1fr]">
        <div className={`md:block ${showFiltersMobile ? 'block' : 'hidden'}`}>
          <Filters
            facets={facets}
            filters={filters}
            onChange={setFilters}
            onClear={() => setFilters(EMPTY_FILTERS)}
            activeCount={activeCount}
          />
        </div>

        <div>
          {pageItems.length === 0 ? (
            <p className="rounded-lg border border-white/10 bg-navy-base/40 p-10 text-center text-ink-secondary">
              {t('bibliography.empty')}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {pageItems.map((entry) => (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  selected={selectedIds.has(entry.id)}
                  onToggleSelect={toggleSelect}
                  onOpen={setDetailEntry}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <Paginator page={page} totalPages={totalPages} onPage={setPage} />
          )}
        </div>
      </div>

      <EntryDetailModal
        onUpdated={(row) => {
          setEntries((prev) => prev?.map((e) => (e.id === row.id ? { ...e, ...row } : e)) ?? prev);
          setDetailEntry((cur) => (cur?.id === row.id ? { ...cur, ...row } : cur));
        }}
        entry={detailEntry}
        onClose={() => setDetailEntry(null)}
        onDeleted={(id) => {
          setEntries((prev) => (prev ? prev.filter((e) => e.id !== id) : prev));
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }}
      />
    </div>
  );
}

